import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/contexts/AuthContext'
import { useMarket } from '@/contexts/MarketContext'
import { useToast } from '@/contexts/ToastContext'
import { ApiError } from '@/services/api/client'
import { positionsApi } from '@/services/api/positions'
import { formatNumber } from '@/lib/format'
import { cn } from '@/lib/cn'
import { TpSlPanel, useTpSlState } from './TpSlPanel'

type OrderType = 'market' | 'limit'

const DEFAULT_LEVERAGE = 100

/**
 * Order ticket → `POST /client/positions` (API_DOCUMENTATION §5.9).
 * Market → status `open`; Limit → status `pending`. Requires trade:full.
 */
export function OrderTicket({ className }: { className?: string }) {
  const { t } = useTranslation(['trading', 'common'])
  const { active } = useMarket()
  const { canTrade } = useAuth()
  const { pushToast } = useToast()
  const [side, setSide] = useState<'buy' | 'sell'>('buy')
  const [orderType, setOrderType] = useState<OrderType>('market')
  const [qty, setQty] = useState('1')
  const [limitPrice, setLimitPrice] = useState('')
  const [leverage, setLeverage] = useState(String(DEFAULT_LEVERAGE))
  const [submitting, setSubmitting] = useState(false)
  const tpSl = useTpSlState()

  const marketPrice = formatNumber(active.last, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 5,
  })

  useEffect(() => {
    if (orderType === 'limit' && !limitPrice) {
      setLimitPrice(String(active.last))
    }
  }, [orderType, active.last, limitPrice])

  const priceDisplay = orderType === 'market' ? marketPrice : limitPrice

  const onSubmit = async () => {
    if (!canTrade) {
      pushToast({
        tone: 'danger',
        title: t('common:auth.readOnlyTitle'),
        message: t('common:auth.readOnlyHint'),
      })
      return
    }
    if (!active.instrumentId) {
      pushToast({
        tone: 'danger',
        title: t('orderTicket.submitFailed'),
        message: t('orderTicket.missingInstrument'),
      })
      return
    }

    const amount = Number(qty)
    const openPrice =
      orderType === 'market' ? active.last : Number(limitPrice)
    const lev = Number(leverage) || DEFAULT_LEVERAGE

    if (!(amount > 0) || !(openPrice > 0)) {
      pushToast({
        tone: 'danger',
        title: t('orderTicket.submitFailed'),
        message: t('orderTicket.invalidQtyPrice'),
      })
      return
    }

    setSubmitting(true)
    try {
      const takeProfit =
        tpSl.tpOpen && Number(tpSl.tp.rate) > 0
          ? Number(tpSl.tp.rate)
          : undefined
      const stopLoss =
        tpSl.slOpen && Number(tpSl.sl.rate) > 0
          ? Number(tpSl.sl.rate)
          : undefined

      await positionsApi.create({
        instrument_id: active.instrumentId,
        side,
        status: orderType === 'market' ? 'open' : 'pending',
        amount,
        open_price: openPrice,
        leverage: lev,
        take_profit: takeProfit,
        stop_loss: stopLoss,
      })

      pushToast({
        tone: 'success',
        title: t('orderTicket.submitSuccess'),
        message: `${active.label} · ${side} · ${amount}`,
      })
      window.dispatchEvent(new CustomEvent('velox:positions-changed'))
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : t('orderTicket.submitFailed')
      pushToast({ tone: 'danger', title: t('orderTicket.submitFailed'), message })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section className={cn('flex flex-col gap-2.5 p-3', className)}>
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-[10px] font-semibold uppercase tracking-wider text-terminal-muted">
          {t('orderTicket.title')}
        </h2>
        <span className="font-mono text-[11px] tabular-nums text-terminal-fg">
          {active.label}
        </span>
      </div>

      {!canTrade ? (
        <p className="rounded border border-danger/30 bg-danger/5 px-2 py-1.5 text-[10px] text-danger">
          {t('common:auth.readOnlyHint')}
        </p>
      ) : null}

      <div className="grid grid-cols-2 gap-1.5">
        <button
          type="button"
          onClick={() => setSide('buy')}
          className={cn(
            'h-8 rounded-sm text-xs font-semibold transition-colors',
            side === 'buy'
              ? 'bg-bid text-white'
              : 'bg-terminal-elevated text-terminal-muted',
          )}
        >
          {t('orderTicket.buy')}
        </button>
        <button
          type="button"
          onClick={() => setSide('sell')}
          className={cn(
            'h-8 rounded-sm text-xs font-semibold transition-colors',
            side === 'sell'
              ? 'bg-ask text-white'
              : 'bg-terminal-elevated text-terminal-muted',
          )}
        >
          {t('orderTicket.sell')}
        </button>
      </div>

      <div
        className="grid grid-cols-2 gap-0.5 rounded-sm border border-terminal-border/40 bg-terminal-elevated/40 p-0.5"
        role="tablist"
        aria-label={t('orderTicket.type')}
      >
        <button
          type="button"
          role="tab"
          aria-selected={orderType === 'market'}
          onClick={() => setOrderType('market')}
          className={cn(
            'h-7 rounded-sm text-[11px] font-semibold transition-colors',
            orderType === 'market'
              ? 'bg-terminal-surface text-terminal-fg shadow-sm'
              : 'text-terminal-muted hover:text-terminal-fg',
          )}
        >
          {t('orderTicket.marketOrder')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={orderType === 'limit'}
          onClick={() => setOrderType('limit')}
          className={cn(
            'h-7 rounded-sm text-[11px] font-semibold transition-colors',
            orderType === 'limit'
              ? 'bg-terminal-surface text-terminal-fg shadow-sm'
              : 'text-terminal-muted hover:text-terminal-fg',
          )}
        >
          {t('orderTicket.limitOrder')}
        </button>
      </div>

      <label className="flex flex-col gap-1 text-[10px] text-terminal-muted">
        {t('orderTicket.quantity')}
        <input
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          inputMode="decimal"
          className={cn(
            'h-8 rounded-sm border border-terminal-border/40 bg-terminal-bg px-2.5',
            'font-mono text-xs tabular-nums text-terminal-fg',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent',
          )}
        />
      </label>

      <label className="flex flex-col gap-1 text-[10px] text-terminal-muted">
        {t('orderTicket.leverage')}
        <input
          value={leverage}
          onChange={(e) => setLeverage(e.target.value)}
          inputMode="numeric"
          className={cn(
            'h-8 rounded-sm border border-terminal-border/40 bg-terminal-bg px-2.5',
            'font-mono text-xs tabular-nums text-terminal-fg',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent',
          )}
        />
      </label>

      <label className="flex flex-col gap-1 text-[10px] text-terminal-muted">
        <span className="flex items-center justify-between gap-2">
          {t('orderTicket.price')}
          {orderType === 'market' ? (
            <span className="font-normal normal-case tracking-normal text-terminal-muted/80">
              {t('orderTicket.marketAuto')}
            </span>
          ) : null}
        </span>
        <input
          value={priceDisplay}
          onChange={(e) => {
            if (orderType === 'limit') setLimitPrice(e.target.value)
          }}
          readOnly={orderType === 'market'}
          inputMode="decimal"
          className={cn(
            'h-8 rounded-sm border border-terminal-border/40 px-2.5',
            'font-mono text-xs tabular-nums text-terminal-fg',
            orderType === 'market'
              ? 'cursor-not-allowed bg-terminal-elevated/50 text-terminal-muted'
              : 'bg-terminal-bg focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent',
          )}
        />
      </label>

      <div className="space-y-1.5">
        <TpSlPanel
          kind="tp"
          open={tpSl.tpOpen}
          onToggle={tpSl.toggleTp}
          values={tpSl.tp}
          onChange={tpSl.setTp}
        />
        <TpSlPanel
          kind="sl"
          open={tpSl.slOpen}
          onToggle={tpSl.toggleSl}
          values={tpSl.sl}
          onChange={tpSl.setSl}
        />
      </div>

      <button
        type="button"
        disabled={submitting || !canTrade}
        onClick={() => void onSubmit()}
        className={cn(
          'mt-0.5 h-9 rounded-sm text-xs font-semibold text-white disabled:opacity-50',
          side === 'buy' ? 'bg-bid hover:bg-bid/90' : 'bg-ask hover:bg-ask/90',
        )}
      >
        {submitting
          ? t('common:actions.working')
          : `${side === 'buy' ? t('orderTicket.buy') : t('orderTicket.sell')} · ${
              orderType === 'market'
                ? t('orderTicket.market')
                : t('orderTicket.limit')
            }`}
      </button>
    </section>
  )
}
